<template>
  <section
    @mouseover="hovering = true" @mouseleave="hovering = false"
    v-if="src"
    class="flex-column">

    <div class="video-inner-container">

      <video
        @click="togglePlay()"
        oncontextmenu="return false;"
        @canplay="videoLoaded($event)"
        @timeupdate="videoTimeUpdate($event)"
        @progress="updateBuffer($event)"
        @pause="paused = true"
        @playing="paused = false"
        preload
        ref="video"
        class="video"
        :class="{ 'player-height': !narrowVideoMode, 'player-height-narrow': narrowVideoMode }"
        :src="src"
        :controls="mobile"
        />

    <div v-if="!mobile" class="video-controls-container" :class="hovering && 'active'">
      <div @click="clickProgressBar" ref="progress-bar-container">
        <div class="progress-bar-container">
          <div ref="progressBar" class="progress-bar" :style="{ width: progressWidth + '%' }"></div>
          <div ref="bufferBar" class="buffer-bar" :style="{ width: bufferWidth + '%' }"></div>
          <div class="marker"
            v-for="comment in comments"
            :style="{ left: (comment.start_time / duration * 100) + '%' }"></div>
        </div>
      </div>
      <div class="video-controls">
        <div class="flex-align-center">

          <ReplayIcon v-if="duration === currentTime" @click.native="replayVideo()" />

          <SimplePlayIcon v-else-if="paused" @click.native="playVideo()" />

          <PauseIcon v-else-if="!paused" @click.native="pauseVideo()" />

          <div class="timestamp">
            {{ currentTime | round | prettyCommentTime }} / {{ duration | round | prettyCommentTime }}
          </div>
        </div>
        <div class="flex-align-center">

          <JumpBackIcon @click.native="jumpBack()"/>

          <JumpForwardIcon @click.native="jumpForward()"/>

          <MutedIcon v-if="muted || volume == '0'" class="video-control mute" @click.native="unMuteVideo()" />

          <SpeakerIcon v-else @click.native="muteVideo()" />

          <input class="volume-control" type="range" min="0" max="1" step="any" v-model="volume"/>

          <select @change='handlePlaybackRateChange'>
            <option>x1</option>
            <option>x1.5</option>
            <option>x2</option>
            <option>x5</option>
          </select>

          <FullScreenIcon @click.native="toggleFullScreen()"/>

        </div>
      </div>
    </div>


    </div>

  </section>

</template>

<script>
import { prettyCommentTime, round } from '../filters.js'
import JumpForwardIcon from './JumpForwardIcon.vue'
import JumpBackIcon from './JumpBackIcon.vue'
import FullScreenIcon from './FullScreenIcon.vue'
import SpeakerIcon from './SpeakerIcon.vue'
import SimplePlayIcon from './SimplePlayIcon.vue'
import ReplayIcon from './ReplayIcon.vue'
import MutedIcon from './MutedIcon.vue'
import PauseIcon from './PauseIcon.vue'

export default {
  name: 'VideoPlayer',
  created () {
    document.addEventListener('keydown', this.handleSpace)
  },
  beforeDestory () {
    document.removeEventListener('keydown', this.handleSpace)
  },
  props: {
    src: {
      type: String,
      required: true,
    },
    poster: {
      type: String,
      required: false,
    },
    comments: {
      type: Array,
      default: [],
    },
    narrowVideoMode: {
      type: Boolean,
      required: false,
    },
  },
  components: {
    JumpForwardIcon,
    JumpBackIcon,
    FullScreenIcon,
    SpeakerIcon,
    SimplePlayIcon,
    ReplayIcon,
    MutedIcon,
    PauseIcon,
  },
  data () {
    return {
      hovering: false,
      duration: 0,
      muted: false,
      paused: true,
      hoveringControls: true,
      volume: 0.5,
      playbackRate: 1,
      currentTime: 0,
      bufferWidth: 0,
    }
  },
  watch: {
    volume (val) {
      const video = this.$refs.video
      video.volume = val
      // if we have a muted volume, we should actually mute the <video>
      if (video.volume > 0) {
        this.muted = false
      }
    },
    muted (val) {
      const video = this.$refs.video
      video.muted = val
      // if the video is muted, we turn the volume to zero to better indicate
      // that the video is muted
      if (video.muted) {
        this.volumeOld = this.volume
        this.volume = 0
      } else {
        this.volume = this.volumeOld
      }
    },
    playbackRate (val) {
      const video = this.$refs.video
      video.playbackRate = val
    },
    paused (shouldPause) {
      if (shouldPause) {
        this.$refs.video.pause()
      } else {
        this.$refs.video.play()
      }
    },
    src () {
      // updates VideoPlayer state so that it is paused on new video load
      this.paused = true
    },
  },
  filters: {
    prettyCommentTime,
    round,
  },
  methods: {
    handleSpace (e) {
      const Space = 32
      const notInInputs = document.activeElement === document.body
      if (e.keyCode === Space && notInInputs) {
        e.preventDefault()
        this.togglePlay()
      }
    },
    replayVideo () {
      const video = this.$refs.video
      video.currentTime = 0
      video.play()
    },
    toggleFullScreen () {
      const video = this.$refs.video
      if (video == null) return
      if (video.webkitRequestFullscreen != null) {
        video.webkitRequestFullscreen()
      } else if (video.mozRequestFullScreen != null) {
        video.mozRequestFullscreen()
      } else if (video.msRequestFullScreen != null) {
        video.msRequestFullscreen()
      }
    },
    handlePlaybackRateChange (e) {
      const rawValue = e.target.value

      const defaultPlaybackRate = 1

      const playbackRate = parseInt(rawValue.replace('x', ''), 10)

      if (!Number.isNaN(playbackRate)) {
        this.playbackRate = playbackRate
      } else {
        this.playbackRate = defaultPlaybackRate
      }
    },
    clickProgressBar (event) {
      const progressBar = this.$refs['progress-bar-container']

      // width defaults to 800
      const totalWidth = progressBar != null
        ? progressBar.offsetWidth
        : 800

      const percentageOfVideo = event.offsetX / totalWidth * this.duration

      const video = this.$refs.video
      video.currentTime = percentageOfVideo
    },
    updateBuffer (e) {
      const buffered = e.target.buffered
      if (buffered) {
        let i = buffered.length
        while (i--) {
          const inBuffer = this.currentTime >= buffered.start(i) && this.currentTime <= buffered.end(i)
          if (inBuffer) {
            const percentBuffered = buffered.end(i) / this.duration * 100
            this.bufferWidth = percentBuffered
          }
        }
      }
    },
    updateCurrentTime (time) {
      this.$emit('currentTime', time)
      this.currentTime = time
    },
    updateDuration (time) {
      // we need to propagate the duration above in order for the video
      // clipping to work
      this.$emit('duration', time)
      this.duration = time
    },
    videoTimeUpdate (e) {
      const currentTime = e.target.currentTime
      const duration = e.target.duration
      this.duration = duration
      this.updateCurrentTime(currentTime)
      this.updateDuration(duration)
    },
    togglePlay () {
      const vid = this.$refs.video
      if (vid == null) return
      if (vid.paused) {
        vid.play()
      } else {
        vid.pause()
      }
    },
    playVideo () {
      this.$refs.video.play()
    },
    pauseVideo () {
      this.$refs.video.pause()
    },
    unMuteVideo () {
      this.muted = false
      if (this.volume === '0') {
        this.volume = 0.2
      }
    },
    muteVideo () {
      this.muted = true
    },
    videoLoaded (e) {
      const video = e.target
      const currentTime = video.currentTime
      const duration = video.duration
      this.updateCurrentTime(currentTime)
      this.updateDuration(duration)
    },
    jumpBack () {
      const jumpIncrement = 10
      this.$refs.video.currentTime -= jumpIncrement
    },
    jumpForward () {
      const jumpIncrement = 10
      this.$refs.video.currentTime += jumpIncrement
    },
  },
  computed: {
    progressWidth () {
      const percentDone = this.currentTime / this.duration * 100
      return percentDone
    },
    mobile () {
      // used for hiding things on mobile
      // a case insensitive regex test
      return /mobile/i.test(window.navigator.userAgent)
    },
  },
}
</script>

<style lang="scss" scoped>
@import '../video-container.scss';

@import '../_variables.scss';

.video-inner-container {
  position: relative;
  display: flex;
  background-color: black;
  justify-content: center;
}

.video-controls-container {
  position: absolute;
  bottom: -40px;

  // necessary for safari
  left: 0;

  color: white;
  width: 100%;
  transition: 0.25s all;
  &.active {
    bottom: 0px;
  }
}

.video-controls {
  min-height: 30px;
  display: flex;
  align-items: center;
  user-select: none;
  background-color: $transparent-gray;
  justify-content: space-between;
}

.video-placeholder-container {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 450px;
  flex-direction: column;
}

.video-control {
  padding: 5px;
  cursor: pointer;
  font-size: 1.5rem;
  width: 30px;
  height: auto;

  &.play,
  &.pause {
    width: 30px;
  }

  &.full-screen,
  &.sound,
  &.mute {
    width: 50px;
  }
}

.progress-bar-container {
  display: flex;
  height: 16px;
  background-color: $background-gray;
  cursor: pointer;
}

$progress-bar-height: 16px;
.progress-bar {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  height: 100%;
  width: 0;
  transition: width 0.3s;
  background-color: $pink;
  z-index: 1;
  position: absolute;
  top: 0;
  left: 0;
  height: $progress-bar-height;
}

.buffer-bar {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  height: 100%;
  width: 0;
  background-color: rgba(230, 230, 230, 0.30);
}

.volume-control {
  // make width depend on its container instead of staying at the default pixel
  // values
  width: 100%;
  margin: 5px;
}

$marker-overlap: 5px;
.marker {
  width: 6px;
  border-top-left-radius: 2px;
  border-top-right-radius: 2px;
  height: $progress-bar-height + $marker-overlap;
  background-color: $teal;
  position: absolute;
  z-index: 10;
  left: 10%;
  top: -$marker-overlap;
  // disable pointer events so clicking the marker doesn't make the video jump
  // to start due to weird calculations
  pointer-events: none;
}

</style>



// WEBPACK FOOTER //
// VideoPlayer.vue?eb2fab50